import { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';

interface PDFViewerModalProps {
  url: string;
  onClose: () => void;
  error?: string | null;
}

export function PDFViewerModal({ url, onClose, error: externalError }: PDFViewerModalProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(externalError ?? '');
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
      setLoading(false);
    }
  }, [externalError]);

  const handleZoomIn = useCallback(() => setScale(prev => Math.min(prev + 0.1, 2)), []);
  const handleZoomOut = useCallback(() => setScale(prev => Math.max(prev - 0.1, 0.5)), []);
  const handleRotate = useCallback(() => setRotation(prev => (prev + 90) % 360), []);

  return (
    <div 
      className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold">PDF Viewer</h2>
          
          <div className="flex items-center space-x-2">            
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-200 rounded-full"
              title="Zoom Out"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-200 rounded-full"
              title="Zoom In"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              onClick={handleRotate}
              className="p-2 hover:bg-gray-200 rounded-full"
              title="Rotate"
            >
              <RotateCw className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-4 bg-gray-50 relative">
          {loading && <LoadingSpinner />}
          {(error || externalError) && <ErrorMessage message={error || externalError || ''} />}
          
          {!error && !externalError && url && (
            <div className="flex justify-center">
              <iframe
                onLoad={() => setLoading(false)}
                src={url}
                className="w-full h-[calc(90vh-8rem)]"
                title="PDF Viewer"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center'
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}